// ============================================================
// TERROR ENGINE — PLAYER ENTITY
// Universal player + companion rendering.
// Used by Plaza, Workshop, FactionHall, Dungeon, and any future zone.
//
// Props:
//   pos        { x, y }         — tile position
//   facing     { x, y }         — direction (-1/0/1)
//   gender     "f" | "m"
//   walkFrame  boolean           — alternates for walk animation
//   isIdle     boolean
//   idleFrame  boolean           — female idle _b frame
//   tile       number            — tile size in px
//   opacity    number            — for dungeon fade effects
//   companionId string | null    — "default" | "jynxi" | null
//   showCompanion boolean
//   size       number            — multiplier (default 1.5 × tile)
//   equipped   { head, face, body, back, aura, title } | null
//                                 — cosmetic overlay; null = no cosmetics rendered
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { getPlayerSprite, getRestSprite, getIdleSprite, getCompanionSprite, getCompanionFallback } from "./SpriteManifest";
import CompanionBody3D, { companionBodyOf } from "./companionBody3D";   // SHIP 10 — the body in the rooms
import { CosmeticOverlay } from "./CosmeticOverlay";

export function PlayerEntity({
  pos,
  facing     = { x: 0, y: 1 },
  gender     = "f",
  walkFrame  = false,
  motion     = null,      // "walk" | "rest" | "idle" — preferred, from usePlayerAnimation
  idleStep   = 0,
  isIdle     = false,     // legacy prop, still honoured
  idleFrame  = false,     // legacy prop, still honoured
  tile       = 48,
  opacity    = 1,
  companionId = null,
  showCompanion = true,
  equipped   = null,
}) {
  const size = Math.floor(tile * 1.5);

  // ── Player sprite resolution ──────────────────────────────
  // Three states, in the order they happen: walking → resting (facing
  // kept, the instant input stops) → true idle (after standing a while).
  // `motion` is the modern prop; the legacy isIdle/idleFrame pair still
  // works so zones can be converted one at a time.
  const phase = motion || (isIdle ? "idle" : "walk");
  const step  = motion ? idleStep : (idleFrame ? 1 : 0);
  const src =
      phase === "idle" ? getIdleSprite(gender, step)
    : phase === "rest" ? getRestSprite(gender, facing.x, facing.y)
    :                    getPlayerSprite(gender, facing.x, facing.y, walkFrame);

  const [imgErr, setImgErr] = useState(false);
  const prevSrc = useRef(src);
  useEffect(() => {
    if (prevSrc.current !== src) { prevSrc.current = src; setImgErr(false); }
  }, [src]);

  // ── Companion position — trails behind player ─────────────
  // companionSrc is the companion's OWN derived art; if that file
  // doesn't exist yet it 404s and we swap to J'ynxi (the prototype)
  // rather than hiding the companion — a room never goes companion-less.
  const ownCompanionSrc = companionId ? getCompanionSprite(companionId) : null;
  const [compFellBack, setCompFellBack] = useState(false);
  const prevCompSrc = useRef(ownCompanionSrc);
  useEffect(() => {
    if (prevCompSrc.current !== ownCompanionSrc) {
      prevCompSrc.current = ownCompanionSrc;
      setCompFellBack(false); // new companion — try its own art again
    }
  }, [ownCompanionSrc]);
  const companionSrc = compFellBack ? getCompanionFallback() : ownCompanionSrc;
  const companionSize = Math.floor(tile * 1.3);
  const fx = facing.x || 0;
  const fy = facing.y || 1;
  const gap = tile * 1.5;
  const compLeft = pos.x * tile - fx * gap * 2 - companionSize / 2 + tile / 2;
  const compTop  = pos.y * tile + (fy === -1 ? gap * 2 : -fy * gap) - companionSize + tile;

  return (
    <>
      {/* Companion */}
      {showCompanion && companionSrc && (
        <div style={{
          position: "absolute",
          left: compLeft, top: compTop,
          width: companionSize, height: companionSize,
          zIndex: 9,
          transition: "left 0.12s, top 0.12s",
          pointerEvents: "none",
          filter: "drop-shadow(0 0 6px #c9a84c44)",
        }}>
          {companionBodyOf(companionId) ? (
            <CompanionBody3D companionId={companionId} size={companionSize} facing={{ x: fx, y: fy }} />
          ) : (
          <img
            key={companionSrc}
            src={companionSrc}
            alt="companion"
            onError={() => { if (!compFellBack) setCompFellBack(true); }}
            style={{ width: "100%", height: "100%", imageRendering: "pixelated" }}
          />
          )}
        </div>
      )}

      {/* Player */}
      <div style={{
        position: "absolute",
        left: pos.x * tile - size / 2 + tile / 2,
        top:  pos.y * tile - size + tile,
        width: size, height: size,
        zIndex: 10,
        transition: "left 0.08s, top 0.08s, opacity 0.3s ease",
        opacity,
        pointerEvents: "none",
      }}>
        {src && !imgErr
          ? <img
              key={src}
              src={src}
              alt="player"
              onError={() => setImgErr(true)}
              style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center bottom", imageRendering: "pixelated" }}
            />
          : <div style={{
              width: "100%", height: "100%",
              background: "#c9a84c",
              borderRadius: 4,
              border: "2px solid #d4a84b",
            }} />
        }

        {/* Cosmetic overlay — no-op when equipped is null */}
        <CosmeticOverlay equipped={equipped} size={size} gender={gender} facing={facing} />
      </div>
    </>
  );
}

// ── Walk / rest / idle animation hook ─────────────────────────
// Drop into any zone's main component. Returns everything <PlayerEntity>
// needs: { walkFrame, motion, idleStep } — plus isIdle/idleFrame for any
// call site still on the old two-state props.
//
// THE THREE STATES
//   walk  — position is changing; walkFrame alternates every step
//   rest  — REST_DELAY after the last step, i.e. the moment the player
//           lets go of the d-pad. Shows the directional rest sprite, so
//           she stays facing the way she was walking instead of snapping
//           to a front-facing pose.
//   idle  — IDLE_DELAY after that, the true idle cycle takes over,
//           advancing one frame every IDLE_TICK. Direction stops
//           mattering here; the cycle itself lives in SpriteManifest.
//
// REST_DELAY is deliberately just longer than one movement tick — long
// enough that a continuous walk never flickers into rest between steps,
// short enough that letting go reads as instant.
export const REST_DELAY = 140;
export const IDLE_DELAY = 3000;
export const IDLE_TICK  = 1800;

export function usePlayerAnimation(pos) {
  const [walkFrame, setWalkFrame] = useState(false);
  const [motion,    setMotion]    = useState("rest");
  const [idleStep,  setIdleStep]  = useState(0);

  const walkFrameRef = useRef(false);
  const lastPosRef   = useRef({ x: pos.x, y: pos.y });
  const restTimer    = useRef(null);
  const idleTimer    = useRef(null);
  const idleAnim     = useRef(null);

  const clearAll = useCallback(() => {
    clearTimeout(restTimer.current);
    clearTimeout(idleTimer.current);
    clearInterval(idleAnim.current);
  }, []);

  useEffect(() => {
    if (pos.x === lastPosRef.current.x && pos.y === lastPosRef.current.y) return;
    lastPosRef.current = { x: pos.x, y: pos.y };

    walkFrameRef.current = !walkFrameRef.current;
    setWalkFrame(walkFrameRef.current);
    setMotion("walk");
    setIdleStep(0);
    clearAll();

    restTimer.current = setTimeout(() => {
      setMotion("rest");
      idleTimer.current = setTimeout(() => {
        setMotion("idle");
        setIdleStep(0);
        idleAnim.current = setInterval(() => setIdleStep(n => n + 1), IDLE_TICK);
      }, IDLE_DELAY);
    }, REST_DELAY);
  }, [pos.x, pos.y, clearAll]);

  // Standing still from the very first render still earns the true idle.
  useEffect(() => {
    idleTimer.current = setTimeout(() => {
      setMotion("idle");
      setIdleStep(0);
      idleAnim.current = setInterval(() => setIdleStep(n => n + 1), IDLE_TICK);
    }, IDLE_DELAY);
    return clearAll;
  }, [clearAll]);

  return {
    walkFrame, motion, idleStep,
    isIdle: motion === "idle",          // legacy
    idleFrame: idleStep % 2 === 1,      // legacy
  };
}

// ── Mobile D-Pad ─────────────────────────────────────────────
// Identical across all zones. One definition, imported everywhere.
export function MobileDpad({ keysRef }) {
  const dirs = [
    { key: "ArrowUp",    label: "▲", col: 2, row: 1 },
    { key: "ArrowLeft",  label: "◀", col: 1, row: 2 },
    { key: "ArrowDown",  label: "▼", col: 2, row: 3 },
    { key: "ArrowRight", label: "▶", col: 3, row: 2 },
  ];
  const press    = (key) => { keysRef.current[key] = true; };
  const release  = (key) => { keysRef.current[key] = false; };
  const releaseAll = () => {
    ["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].forEach(k => { keysRef.current[k] = false; });
  };
  const btn = (col, row) => ({
    gridColumn: col, gridRow: row,
    width: 48, height: 48,
    background: "rgba(10,5,30,0.85)",
    border: "1px solid rgba(161,130,250,0.4)",
    borderRadius: 10, color: "rgba(200,180,255,0.9)", fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", userSelect: "none", WebkitUserSelect: "none",
    touchAction: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.7)",
  });
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 20, zIndex: 200,
      display: "grid",
      gridTemplateColumns: "48px 48px 48px",
      gridTemplateRows: "48px 48px 48px",
      gap: 5, touchAction: "none",
    }}>
      {dirs.map(({ key, label, col, row }) => (
        <div key={key} style={btn(col, row)}
          onPointerDown={e => { e.preventDefault(); press(key); }}
          onPointerUp={e => { e.preventDefault(); release(key); }}
          onPointerLeave={e => { e.preventDefault(); release(key); }}
          onPointerCancel={e => { e.preventDefault(); releaseAll(); }}>
          {label}
        </div>
      ))}
    </div>
  );
}
